import { Activity, CircleUserRound, LogOut, Menu, Network, Radio, Settings, UsersRound, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./auth";
import { IconButton, Logo, SelectChevron } from "./components";
import { QuickOpen, QuickOpenTrigger } from "./QuickOpen";

const baseNavigation = [
  { to: "/app", end: true, label: "Pulse", icon: Activity },
  { to: "/app/loops", label: "Loops", icon: Network },
  { to: "/app/onboarding", label: "Sources", icon: Radio },
  { to: "/app/people", label: "Group", icon: UsersRound },
  { to: "/app/settings", label: "Settings", icon: Settings },
];

export function AppShell() {
  const { user, organizations, activeOrganization, setActiveOrganizationId, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const navigation = baseNavigation.map((item) => item.to === "/app/people" && activeOrganization?.kind === "personal" ? { ...item, label: "Sharing" } : item);
  const workspaceLabel = activeOrganization?.kind === "personal" ? "Private workspace" : "Current group";

  useEffect(() => setOpen(false), [location.pathname]);

  // The palette and the navigation drawer never stack: opening one closes the other.
  const openSearch = (value: boolean) => {
    if (value) setOpen(false);
    setSearchOpen(value);
  };

  useEffect(() => {
    if (!open) return;
    const focusableSelector = 'a[href], button:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const focusFirst = window.requestAnimationFrame(() => sidebarRef.current?.querySelector<HTMLElement>(focusableSelector)?.focus());
    const containNavigation = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        menuButtonRef.current?.focus();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(sidebarRef.current?.querySelectorAll<HTMLElement>(focusableSelector) ?? [])
        .filter((element) => element.getClientRects().length > 0);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", containNavigation);
    return () => {
      window.cancelAnimationFrame(focusFirst);
      window.removeEventListener("keydown", containNavigation);
    };
  }, [open]);

  return (
    <div className="app-shell">
      <header className="mobile-bar">
        <Logo compact />
        <div className="mobile-bar__context"><span>{workspaceLabel}</span><strong>{activeOrganization?.name}</strong></div>
        <QuickOpenTrigger variant="icon" onOpen={() => openSearch(true)} />
        <IconButton ref={menuButtonRef} className="icon-button--large" onClick={() => setOpen((value) => !value)} label={open ? "Close navigation" : "Open navigation"} aria-expanded={open} aria-controls="app-navigation">{open ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}</IconButton>
      </header>
      <aside ref={sidebarRef} id="app-navigation" className={`sidebar receiver ${open ? "sidebar--open" : ""}`} role={open ? "dialog" : undefined} aria-modal={open ? "true" : undefined} aria-label="Workspace navigation">
        <div className="sidebar__top">
          <Logo />
          <div className="organization-picker">
            <span className="organization-picker__glyph">{activeOrganization?.name.slice(0, 1).toUpperCase()}</span>
            <label>
              <span>{workspaceLabel}</span>
              <select value={activeOrganization?.id ?? ""} onChange={(event) => setActiveOrganizationId(event.target.value)}>
                {organizations.map((organization) => <option key={organization.id} value={organization.id}>{organization.kind === "personal" ? `Private · ${organization.name}` : organization.name}</option>)}
              </select>
              <SelectChevron />
            </label>
          </div>
          <QuickOpenTrigger onOpen={() => openSearch(true)} />
          <nav aria-label="Primary">
            {navigation.map(({ to, label, icon: Icon, end }) => (
              <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)} className={({ isActive }) => (isActive ? "nav-link nav-link--active" : "nav-link")}>
                <Icon size={17} />
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>
        </div>
        <div className="sidebar__bottom">
          <div className="user-chip">
            <CircleUserRound size={21} />
            <div><strong>{user?.displayName}</strong><span>{user?.email}</span></div>
          </div>
          <button
            className="signout"
            type="button"
            onClick={() => {
              void signOut().then(() => navigate("/"));
            }}
          >
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </aside>
      {open && <button className="sidebar-scrim" type="button" onClick={() => { setOpen(false); menuButtonRef.current?.focus(); }} aria-label="Close navigation" aria-controls="app-navigation" />}
      <main className="app-main" id="main-content"><Outlet /></main>
      <QuickOpen open={searchOpen} onOpenChange={openSearch} />
    </div>
  );
}
